import React from 'react';
import NumberFlowReact, { useCanAnimate } from '@number-flow/react';

import { Root } from './GaugeNumber.styles';

export type GaugeNumberProps = {
  /** The score the figure rolls to. */
  value: number;
  /**
   * Reveal gate. While false the figure holds at 0, so the roll doesn't play
   * unseen behind a skeleton; when it flips true the figure rolls 0 -> value.
   * Pass a flag that turns true once the gauge is on screen.
   */
  animate: boolean;
  /** Stagger the roll start (ms) after `animate` turns true, so a row of gauges
   *  counts up in sequence. */
  delay?: number;
  className?: string;
};

/**
 * A gauge's centred figure, rolled in with the same NumberFlow odometer animation
 * the Stats dashboard uses for its headline numbers (mirrors the stats `LiveNumber`
 * and home `StatValue`): it counts up from 0 to `value` on reveal and tweens
 * between values on any later update.
 *
 * NumberFlow only animates on a value *change*, so the entrance is driven by
 * holding the displayed value at 0 until `animate` turns true, then bumping it to
 * the target (after `delay`). Under reduced motion the figure shows `value`
 * straight away — no roll, no flash of 0 — and keeps following later updates.
 */
export const GaugeNumber = ({ value, animate, delay = 0, className }: GaugeNumberProps): React.ReactElement => {
  const canAnimate = useCanAnimate({ respectMotionPreference: true });
  const [display, setDisplay] = React.useState(() => (canAnimate ? 0 : value));

  React.useEffect(() => {
    // Reduced motion (or no animation support): snap to the value and keep
    // following later updates without a roll.
    if (!canAnimate) {
      setDisplay(value);
      return undefined;
    }
    // Not revealed yet: hold at 0 so the roll begins when the gauge is visible.
    if (!animate) return undefined;
    const id = window.setTimeout(() => setDisplay(value), delay);
    return () => window.clearTimeout(id);
  }, [canAnimate, animate, value, delay]);

  return (
    <Root className={className}>
      <NumberFlowReact value={display} format={{ maximumFractionDigits: 0 }} locales="en-US" animated={canAnimate} />
    </Root>
  );
};

export default GaugeNumber;
